import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import PolicyLink from "@/components/PolicyLink";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { EmergencyNotice } from "@/components/incidents/EmergencyNotice";
import api, { formatApiError } from "@/lib/api";
import { INCIDENT_CATEGORIES, INCIDENT_SEVERITIES } from "@/config/incidentCatalog";

export default function IncidentNewPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [children, setChildren] = useState([]);
  const [saving, setSaving] = useState(false);
  const [missing, setMissing] = useState([]);
  const [form, setForm] = useState({
    child_id: params.get("child") || "",
    incident_date: new Date().toISOString().slice(0, 10),
    incident_time: "", location: "", direction: "", category: "", severity: "low",
    description: "", immediate_action: "", everyone_safe: null,
    needs_immediate_notification: false, programme_id: params.get("programme") || null,
    session_id: params.get("session") || null, journey_plan_id: params.get("plan") || null,
  });
  const [extra, setExtra] = useState({
    immediate_outcome: "", people_involved: "", witnesses: "", what_could_have_happened: "",
    contributing_factors: "", recommended_prevention: "", injury: false, emergency_services: false,
    parent_contacted: false, school_contacted: false, follow_up_required: false,
    risk_review_required: false, journey_plan_review_required: false, safeguarding_escalation: false,
  });
  const [showExtra, setShowExtra] = useState(false);

  useEffect(() => {
    api.get("/children").then((r) => setChildren(r.data.items || [])).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setX = (k, v) => setExtra((f) => ({ ...f, [k]: v }));

  const save = async (submit) => {
    setMissing([]);
    if (!form.child_id) {
      if (submit) {
        setMissing(["Child / case"]);
      } else {
        toast.error("Select the child this incident relates to");
      }
      return;
    }
    setSaving(true);
    setMissing([]);
    try {
      const { data } = await api.post("/incidents", { ...form, ...extra, submit });
      toast.success(submit ? `Incident ${data.incident_number} submitted` : "Draft saved — complete details as soon as possible");
      navigate(`/incidents/${data.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.missing) setMissing(detail.missing);
      toast.error(typeof detail === "object" ? detail.message : formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const isNearMiss = form.category === "near_miss";

  return (
    <div className="space-y-4 max-w-2xl pb-24" data-testid="incident-new-page">
      <Link to="/incidents" data-testid="incident-back-link" className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
        <ArrowLeft className="h-4 w-4" /> Back to incidents
      </Link>
      <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Quick incident report</h1>
      <PolicyLink module="incidents" />
      <EmergencyNotice />
      <p className="text-sm text-slate-600">
        Record the essentials now. Incomplete reports are marked <b>Follow-up information required</b> — finish them as
        soon as reasonably possible. You are automatically recorded as the reporter.
      </p>

      {missing.length > 0 && (
        <div data-testid="incident-completeness-errors" role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
          <p className="font-semibold">Incident is incomplete — missing:</p>
          <ul className="list-disc pl-5">{missing.map((m) => <li key={m}>{m}</li>)}</ul>
        </div>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Essential information</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label>Child / case *</Label>
            <Select value={form.child_id} onValueChange={(v) => set("child_id", v)}>
              <SelectTrigger data-testid="incident-child" className="h-12"><SelectValue placeholder="Select child" /></SelectTrigger>
              <SelectContent className="bg-white">
                {children.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.surname} · {c.case_id}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Date *</Label><Input data-testid="incident-date" type="date" className="h-12" value={form.incident_date} onChange={(e) => set("incident_date", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Time</Label><Input data-testid="incident-time" type="time" className="h-12" value={form.incident_time} onChange={(e) => set("incident_time", e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label>Location *</Label><Input data-testid="incident-location" className="h-12" value={form.location} onChange={(e) => set("location", e.target.value)} /></div>
          <div className="space-y-1.5">
            <Label>Category *</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="incident-category" className="h-12"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent className="bg-white">
                {INCIDENT_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Initial severity (triage indicator — managers review)</Label>
            <div className="grid grid-cols-4 gap-2">
              {INCIDENT_SEVERITIES.map((s) => (
                <button key={s.value} type="button" data-testid={`incident-severity-${s.value}`} onClick={() => set("severity", s.value)}
                  className={`h-12 rounded-md border text-sm font-semibold transition-colors duration-150 ${form.severity === s.value ? (s.value === "critical" ? "bg-red-600 text-white border-red-600" : "bg-primary text-white border-primary") : "bg-white text-slate-700 border-border hover:bg-muted"}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{isNearMiss ? "What happened (near miss) *" : "What happened *"}</Label>
            <Textarea data-testid="incident-description" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} />
          </div>
          <div className="space-y-1.5"><Label>Immediate action taken *</Label>
            <Textarea data-testid="incident-action" rows={2} value={form.immediate_action} onChange={(e) => set("immediate_action", e.target.value)} /></div>
          <div className="space-y-1.5">
            <Label>Is everyone currently safe? *</Label>
            <div className="grid grid-cols-2 gap-2">
              {[["yes", true], ["no", false]].map(([k, v]) => (
                <button key={k} type="button" data-testid={`incident-safe-${k}`} onClick={() => set("everyone_safe", v)}
                  className={`h-12 rounded-md border text-sm font-semibold transition-colors duration-150 ${form.everyone_safe === v ? (v ? "bg-ukgreen text-white border-ukgreen" : "bg-red-600 text-white border-red-600") : "bg-white text-slate-700 border-border hover:bg-muted"}`}>
                  {v ? "Yes — everyone safe" : "No / not sure"}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
            <span className="text-sm font-medium text-slate-700">Manager needs immediate notification?</span>
            <div className="flex gap-1">
              {[true, false].map((v) => (
                <button key={String(v)} type="button" data-testid={`incident-notify-${v}`} onClick={() => set("needs_immediate_notification", v)}
                  className={`h-10 px-5 rounded-md text-sm font-semibold ${form.needs_immediate_notification === v ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>
                  {v ? "Yes" : "No"}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <button type="button" data-testid="incident-more-details-toggle" onClick={() => setShowExtra((s) => !s)}
        className="text-sm text-primary font-medium hover:underline">
        {showExtra ? "Hide additional details" : "Add more details now (optional)"}
      </button>

      {showExtra && (
        <Card>
          <CardHeader><CardTitle className="text-base">Additional details</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {isNearMiss && (
              <>
                <div className="space-y-1.5"><Label>What could have happened</Label><Textarea data-testid="incident-could-have" rows={2} value={extra.what_could_have_happened} onChange={(e) => setX("what_could_have_happened", e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Contributing factors</Label><Textarea data-testid="incident-factors" rows={2} value={extra.contributing_factors} onChange={(e) => setX("contributing_factors", e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Recommended prevention</Label><Textarea data-testid="incident-prevention" rows={2} value={extra.recommended_prevention} onChange={(e) => setX("recommended_prevention", e.target.value)} /></div>
              </>
            )}
            <div className="space-y-1.5"><Label>Immediate outcome</Label><Textarea data-testid="incident-outcome" rows={2} value={extra.immediate_outcome} onChange={(e) => setX("immediate_outcome", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>People involved</Label><Input data-testid="incident-people" value={extra.people_involved} onChange={(e) => setX("people_involved", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Witnesses</Label><Input data-testid="incident-witnesses" value={extra.witnesses} onChange={(e) => setX("witnesses", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-2">
              {[["injury", "Injury"], ["emergency_services", "Emergency services"], ["parent_contacted", "Parent/carer contacted"], ["school_contacted", "School contacted"]].map(([k, label]) => (
                <button key={k} type="button" data-testid={`incident-flag-${k}`} onClick={() => setX(k, !extra[k])}
                  className={`h-12 rounded-md border text-xs font-medium transition-colors duration-150 ${extra[k] ? "bg-primary text-white border-primary" : "bg-white text-slate-700 border-border hover:bg-muted"}`}>
                  {label}: {extra[k] ? "Yes" : "No"}
                </button>
              ))}
            </div>
            <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-semibold text-amber-900">Review flags (create management work items — approved records are never silently changed)</p>
              {[["risk_review_required", "Risk assessment review required"], ["journey_plan_review_required", "Route plan review required"], ["safeguarding_escalation", "Safeguarding escalation required"]].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-amber-900">
                  <input type="checkbox" data-testid={`incident-review-${k}`} checked={extra[k]} onChange={(e) => setX(k, e.target.checked)} className="h-4 w-4" />
                  {label}
                </label>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-white/95 backdrop-blur px-4 py-3">
        <div className="mx-auto flex max-w-2xl gap-2">
          <Button variant="outline" className="h-12 flex-1 font-semibold" data-testid="incident-save-draft" disabled={saving} onClick={() => save(false)}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save draft"}
          </Button>
          <Button className="h-12 flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold" data-testid="incident-submit" disabled={saving} onClick={() => save(true)}>
            Submit incident
          </Button>
        </div>
      </div>
    </div>
  );
}
